import React, { useContext, useEffect, useState } from "react"
import { navigate } from "gatsby"
import { GatsbyImage } from "gatsby-plugin-image"
import { getShopifyImage } from "gatsby-source-shopify"
import {
	HandbagIcon,
	InfoIcon,
	MagnifyingGlassIcon,
} from "@phosphor-icons/react"
import AddToWishListButton from "@components/common/AddToWishListButton"
import SkeletonProductCard from "@components/skeletons/SkeletonProductCard"
import Tag from "@components/common/Tag"
import QuickBuyContext from "@context/QuickBuyContext"
import { trimStorefrontId, productFormattedPrice } from "@utils"
import { StoreContext } from "@context/StoreContext"
import { sendGAEvent } from "@utils/analytics"
import { GA_EVENTS } from "@utils/analytics/constants"
import Tooltip from "@components/common/Tooltip"

// assets
import zeroQtyPlaceHolder from "@images/Products/no-longer-available-placeholder.webp"
import clsx from "clsx"

// Still contemplating whether to install propTypes as it may possibly conflict with gatsby-image libraries' own propTypes

/** cardOptions 
// no-tag - hides tags (if available)
// no-wishlist - hides wishlist button 
// no-buttons - hides add-to-cart and quickbuy now buttons
**/

let activeCardRef = { deactivate: null }

const DynamicProductCard = ({
	data,
	cardOptions = "",
	style = "classic",
	cardStyles,
	width,
	height,
	isSwiping = false,
}) => {
	const [hovered, setHovered] = useState(false)
	const [productLoaded, setProductLoaded] = useState(false)
	const [hoverProductLoaded, setHoverProductLoaded] = useState(false)
	const [isTouched, setIsTouched] = useState(false)
	const { setQuickBuyData, setAddToBagClicked } = useContext(QuickBuyContext)
	const { checkout, currencySelected } = React.useContext(StoreContext)
    const [price, setPrice] = useState({
        amount: 0,
        compareAtPrice: 0,
        currencyCode: process.env.GATSBY_STORE !== "PH" ? "USD" : "PHP"
    });

	const cardOpt = cardOptions.split(" ")
	const hasQuantity =
		data?.variants?.[0]?.inventoryQuantity > 0 ||
		!data?.variants?.[0]?.inventoryQuantity

	const handleProductClick = (handle) => {
		if (hovered && !isTouched) {
			// navigate(`/products/${data?.name}`)
			navigate(`/products/${handle}`)
		} else {
			sendGAEvent(GA_EVENTS.SELECT_CONTENT, {
				content_type: "product_card",
				content_id: data?.id,
			})
		}
	}

	const handleQuickBuy = () => {
		console.log("Quick Buy")
	}

	const handleMouseEnter = () => {
		if (!hasQuantity) return

		setHovered(true)
	}

	const handleInitialTouch = () => {
		if (!hasQuantity) return

		if (activeCardRef.deactivate) {
			activeCardRef.deactivate()
		}
		setIsTouched(true)
		activeCardRef.deactivate = () => setIsTouched(false)
	}

	const handleSecondTouch = (handle) => {
		navigate(`/products/${handle}`)
	}

	const isFinalSale = data?.title?.split(" / ")?.[1]?.includes("FINAL SALE")
	const isDiscounted =
		data?.priceRangeV2?.minVariantPrice?.amount !==
		data?.variants?.[0]?.compareAtPrice
			? true
			: false

	const originalImage = React.useMemo(() => {
		const imageSrc = data?.media?.[1]?.preview?.image?.src

		if (!imageSrc) {
			//console.warn("Image source is undefined, falling back to placeholder or null.");
			return null // Or return a placeholder image object here if needed
		}

		const image = {
			originalSrc: data?.media?.[0]?.preview?.image?.src,
			width: data?.media?.[0]?.preview?.image?.gatsbyImageData?.width,
			height: data?.media?.[0]?.preview?.image?.gatsbyImageData?.height,
		}

		return getShopifyImage({
			image: image,
			layout: "constrained",
			crop: "contain",
			width: width,
			height: height,
		})
	}, [data?.media?.[0]?.preview?.image?.src])

	const hoveredImage = React.useMemo(() => {
		const imageSrc = data?.media?.[1]?.preview?.image?.src

		if (!imageSrc) {
			//console.warn("Image source is undefined, falling back to placeholder or null.");
			return null // Or return a placeholder image object here if needed
		}

		const image = {
			originalSrc: data?.media?.[1]?.preview?.image?.src,
			width: data?.media?.[1]?.preview?.image?.gatsbyImageData?.width,
			height: data?.media?.[1]?.preview?.image?.gatsbyImageData?.height,
		}

		return getShopifyImage({
			image: image,
			layout: "constrained",
			crop: "contain",
			width: width,
			height: height,
		})
	}, [data?.media?.[1]?.preview?.image?.src, width, height])

	const hasComingSoon = data?.collections?.some(
		(collection) => collection?.handle === "coming-soon"
	)

	const badgeTags = data?.tags?.filter((tag) => tag.startsWith("badge_"))
	const isBratz = badgeTags?.includes("badge_BRATZ®")

	const calculateDiscount = (compareAtPrice, price) => {
		if (!compareAtPrice || compareAtPrice <= price) return 0

		return Math.round(((compareAtPrice - price) * 100) / compareAtPrice)
	}

	const isPreOrder = badgeTags?.includes("badge_pre-order")

    useEffect(() => {
        const fetchProductWithContextualPricing = async (handle, countryCode) => {

            const response = await fetch(`/get-product?handle=${handle}&country=${countryCode}`);

            const result = await response.json();

            setPrice({
                amount: result?.product?.data?.productByHandle?.variants?.edges?.[0]?.node?.contextualPricing?.price?.amount,
                currencyCode: result?.product?.data?.productByHandle?.variants?.edges?.[0]?.node?.contextualPricing?.price?.currencyCode,
                compareAtPrice: result?.product?.data?.productByHandle?.variants?.edges?.[0]?.node?.contextualPricing?.compareAtPrice?.amount,
            })
            
        };

        fetchProductWithContextualPricing(data?.handle, currencySelected?.countryCode || "US");
    }, [data, currencySelected]);

	return (
		<React.Fragment>
			{(!productLoaded || !hoverProductLoaded) && (
				<div className="relative z-20">
					<SkeletonProductCard />
				</div>
			)}
			<div
				className={clsx(
					"relative group w-full",
					style === "classic"
						? "w-min"
						: cardStyles !== null
						? cardStyles
						: style === "compact" && "!w-[104px] h-30 text-xs",
					(!productLoaded || !hoverProductLoaded) && "pointer-events-none"
				)}
				onMouseEnter={() => handleMouseEnter()}
				onMouseLeave={() => setHovered(false)}
				onTouchEnd={() => handleInitialTouch()}
			>
				<div className="w-full absolute px-3 pt-3 flex justify-between cursor-pointer z-10">
					{isDiscounted ? (
						<div className="flex">
							<Tag
								className={`text-xs md:text-sm`}
								label={`SAVE ${calculateDiscount(
									data?.variants?.[0]?.compareAtPrice,
									data?.priceRangeV2?.minVariantPrice?.amount
								)} %`}
							/>
						</div>
					) : badgeTags?.length > 0 ? (
						isPreOrder ? (
							<div className="flex">
								<Tag className={`text-xs md:text-sm`} label={"Pre Order"} />
							</div>
						) : isBratz ? (
							<div className="flex">
								<Tag className={`text-xs md:text-sm`} label={"BRATZ®"} />
							</div>
						) : (
							<div className="flex">
								<Tag
									className={`text-xs md:text-sm`}
									label={badgeTags?.[0]
										?.replace("badge_", "")
										?.replace("-", " ")}
								/>
							</div>
						)
					) : null}
					{!cardOpt.includes("no-wishlist") && (
						<AddToWishListButton className="text-black ml-auto" data={data} />
					)}
				</div>
				<div
					className={clsx(
						"relative cursor-pointer overflow-hidden w-full mb-1",
						cardStyles !== null
							? cardStyles
							: style === "compact"
							? "h-30"
							: "h-[300px] md:h-[320px] lg:h-[390px]"
					)}
					aria-label="checked-product-details"
					onClick={() => {
						handleProductClick(data?.handle)
					}}
					onTouchEnd={() => {
						if (isTouched && !isSwiping) handleSecondTouch(data?.handle)
					}}
				>
					{!hasQuantity ? (
						<div
							className="w-full lg:max-h-[390px] h-[390px] object-cover md:object-contain"
							style={{ height: `${height}px`, width: `${width}px` }}
						>
							<img
								className="w-full object-cover"
								src={zeroQtyPlaceHolder}
								alt="Product No Longer Available"
							/>
						</div>
					) : (
						<>
							<a
								className={`relative block w-full lg:max-h-[390px] h-[390px]`}
								style={{ height: `${height}px`, width: `${width}px` }}
								href={`/products/${data?.handle}`}
								onClick={(e) => e.preventDefault()}
							>
								<GatsbyImage
									className={clsx(
										"absolute border-gray-200 w-full h-full lg:max-h-[390px] object-cover transition-all duration-300 ease-out",
										hovered || isTouched
											? "opacity-0 border-2"
											: "opacity-100 border-0",
										style === "classic"
											? "productcard-classic"
											: style === "compact"
											? "productcard-compact"
											: "productcard-classic"
									)}
									alt={data?.altText || data?.title}
									image={originalImage}
									style={{ position: "absolute" }}
									onLoad={() => setProductLoaded(true)}
								/>

								<GatsbyImage
									className={clsx(
										"absolute border-gray-200 w-full h-full lg:max-h-[390px] object-cover transition-all duration-300 ease-out",
										hovered || isTouched
											? "opacity-100 border-2"
											: "opacity-[0.01] border-0",
										style === "classic"
											? "productcard-classic"
											: style === "compact"
											? "productcard-compact"
											: "productcard-classic"
									)}
									alt={data?.altText || data?.title}
									image={hoveredImage}
									style={{ position: "absolute" }}
									onLoad={() => setHoverProductLoaded(true)}
								/>
							</a>

							{style !== "compact" && (
								<div
									className={clsx(
										"absolute w-full flex justify-center items-center bg-black bg-opacity-30 h-16 left-0 text-white",
										hovered || isTouched ? "bottom-0" : "-bottom-20"
									)}
								>
									<MagnifyingGlassIcon size={32} weight="light" />
								</div>
							)}
						</>
					)}
				</div>

				{/* LABELS AND PRICING */}
				<div className={clsx(style === "compact" && "space-y-1", (!productLoaded || !hoverProductLoaded) && "hidden")}>
					<div className="product-name">{data?.title?.split(" / ")[0]}</div>
					<div className="product-sub">
						{data?.title?.split(" / ")[1]?.replace("FINAL SALE", "")}
					</div>
					{!hasQuantity ? (
						<div className="w-full flex flex-nowrap justify-between items-center text-[#888888] mt-2.5">
							<p className="text-sm">Product No Longer Available</p>
							<Tooltip
								maxWidth="max-w-48"
								text={
									"This product is currently not available because it may have been updated in our store or is temporarily out of stock."
								}
								offsetBubblePointer="top-[3.75rem]"
							>
								<InfoIcon size={20} />
							</Tooltip>
						</div>
					) : (
						<section className="flex flex-wrap justify-start items-center mt-1 gap-x-2">
							{style === "compact" ? (
								<span className="product-price !text-sm">
									{price?.currencyCode
										? productFormattedPrice(
												price?.currencyCode,
												price?.amount
										  )
										: null}
								</span>
							) : (
								<>
									<span className="product-price">
										{price?.currencyCode
											? productFormattedPrice(
                                                price?.currencyCode,
												price?.amount
											  )
											: null}
									</span>
									{isDiscounted && (
										<span className="discounted">
											{price?.currencyCode
												? productFormattedPrice(
													price?.currencyCode,
													price?.compareAtPrice
												  )
												: null}
										</span>
									)}
									{isFinalSale && (
										<>
											<span className="hidden md:block text-[1.5rem] font-normal text-[#D1D1D1]">
												|
											</span>
											<span className="text-danger font-medium basis-full md:basis-auto">
												FINAL SALE
											</span>
										</>
									)}
									{hasComingSoon && (
										<>
											<span className="hidden md:block md:text-[1.5rem] font-normal text-[#D1D1D1]">
												|
											</span>
											<span className="font-medium basis-full md:basis-auto">
												COMING SOON
											</span>
										</>
									)}
								</>
							)}
						</section>
					)}
				</div>
				{!cardOpt.includes("no-buttons") && !hasComingSoon && hasQuantity && (
					<section
						className={clsx(
							"flex flex-nowrap justify-between gap-x-2 mt-2",
							style === "classic" &&
								`transform opacity-0 group-hover:opacity-100 transition-opacity ${
									isTouched && "opacity-100"
								}`
						)}
					>
						{style !== "compact" ? (
							<>
								<button
									className={`btn-primary flex-1 ${trimStorefrontId(
										data?.variants?.[0]?.storefrontId,
										"pid-"
									)}`}
									onClick={() => setQuickBuyData(data)}
									aria-label="Quick Buy"
									data-pid={trimStorefrontId(
										data?.variants?.[0]?.storefrontId,
										"pid-"
									)}
								>
									Quick Buy
								</button>
								<button
									className={`btn-outline ${trimStorefrontId(
										data?.variants?.[0]?.storefrontId,
										"pid-"
									)}`}
									onClick={() => setAddToBagClicked(data)}
									aria-label="Quick Add to Bag"
									data-pid={trimStorefrontId(
										data?.variants?.[0]?.storefrontId,
										"pid-"
									)}
								>
									<HandbagIcon size={16} weight="light" />
								</button>
							</>
						) : (
							<>
								<button
									className={`btn-outline flex-1 ${trimStorefrontId(
										data?.variants?.[0]?.storefrontId,
										"pid-"
									)}`}
									onClick={() => handleQuickBuy()}
									aria-label="Quick Add To Bag"
									data-pid={trimStorefrontId(
										data?.variants?.[0]?.storefrontId,
										"pid-"
									)}
								>
									Add To Bag
								</button>
							</>
						)}
					</section>
				)}
			</div>
		</React.Fragment>
	)
}

export default DynamicProductCard
